// Single unified markdown previewer for the workspace.
//
// Workspace-project pages (tickets, docs, knowledge-base) own their page-level
// H1/H2 chrome, so user-supplied markdown must never claim those heading slots.
// When `demoteHeadings` is true (default): h1→H4, h2→H5, h3-h6→H6.
// When false: native H1-H6 are used (useful for stand-alone surfaces where the
// markdown IS the primary content).
//
// Props:
//   text           — the markdown source string
//   withMentions   — default false; when true applies @mention / #channel highlighting
//   demoteHeadings — default true; h1→H4, h2→H5, h3-h6→H6
//
// Internal rendering delegates to transformMarkdown from symbolize-markdown.
// Caches on el._lastMd to skip unchanged updates.
//
// Chat-specific highlights (##ticket, @@event, +reference) are always applied
// when the body contains the respective patterns — they only fire when the regex
// matches so they are safe for non-chat surfaces (tickets, docs).

import { transformMarkdown } from '@symbo.ls/symbolize-markdown'
import { highlightMentions, highlightChannels } from '../functions/chat/highlightMentions.js'
import { getSDK } from '../functions/sdk.js'

// Component maps — built once, reused on every render call.
const _DEMOTE_COMPONENTS = {
  em: 'Em', h1: 'H4', h2: 'H5', h3: 'H6', h4: 'H6', h5: 'H6', h6: 'H6',
  hr: 'Hr', li: 'LI', ol: 'OL', ul: 'UL', del: 'Del', img: 'Img',
  pre: 'Pre', code: 'InlineCode', link: 'Link', strong: 'Strong',
  codeBlock: 'CodeBlock', paragraph: 'P', blockquote: 'Blockquote'
}

const _NATIVE_COMPONENTS = {
  em: 'Em', h1: 'H1', h2: 'H2', h3: 'H3', h4: 'H4', h5: 'H5', h6: 'H6',
  hr: 'Hr', li: 'LI', ol: 'OL', ul: 'UL', del: 'Del', img: 'Img',
  pre: 'Pre', code: 'InlineCode', link: 'Link', strong: 'Strong',
  codeBlock: 'CodeBlock', paragraph: 'P', blockquote: 'Blockquote'
}

// ── Reference-card helpers (shared with renderMarkdown.js) ─────────────────
const _CARD_BASE = 'display:inline-flex;align-items:center;gap:6px;padding:3px 8px;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;vertical-align:middle;text-decoration:none;'
const _CARD_STYLES = {
  ticket: _CARD_BASE + 'color:var(--color-orange,#f59e0b);background:rgba(245,158,11,0.1);',
  event: _CARD_BASE + 'color:var(--color-blue,#6366f1);background:rgba(99,102,241,0.1);',
  reference: _CARD_BASE + 'color:var(--color-green,#22c55e);background:rgba(34,197,94,0.1);'
}
const _CARD_ICONS = { ticket: '■', event: '○', reference: '◆' }

const _replaceTextNodes = (el, regex, makeSpan) => {
  if (!el || !el.node) return []
  const walker = document.createTreeWalker(el.node, NodeFilter.SHOW_TEXT)
  const replacements = []
  let textNode
  while ((textNode = walker.nextNode())) {
    regex.lastIndex = 0
    if (!regex.test(textNode.textContent)) continue
    replacements.push(textNode)
  }
  const spans = []
  for (const node of replacements) {
    if (!node.parentNode) continue
    const frag = document.createDocumentFragment()
    let last = 0
    regex.lastIndex = 0
    let match
    while ((match = regex.exec(node.textContent)) !== null) {
      if (match.index > last) frag.appendChild(document.createTextNode(node.textContent.slice(last, match.index)))
      const span = makeSpan(match)
      spans.push(span)
      frag.appendChild(span)
      last = match.index + match[0].length
    }
    if (last < node.textContent.length) frag.appendChild(document.createTextNode(node.textContent.slice(last)))
    node.parentNode.replaceChild(frag, node)
  }
  return spans
}

const _TICKET_RE = /##(\d+)/g
const _highlightTickets = (el) => {
  const spans = _replaceTextNodes(el, _TICKET_RE, (match) => {
    const num = parseInt(match[1], 10)
    const span = document.createElement('span')
    span.style.cssText = _CARD_STYLES.ticket
    span.setAttribute('data-ref-ticket', String(num))
    span.innerHTML = `<span>${_CARD_ICONS.ticket}</span> #${num}`
    return span
  })
  if (!spans.length) return
  const numbers = spans.map(s => parseInt(s.getAttribute('data-ref-ticket'), 10))
  // migrated 2026-04-27 to SDK route workspaceProject.tickets/list
  getSDK().execute('workspaceProject.tickets', 'list', {
    filter: { deleted: false, number_in: [...new Set(numbers)] },
    options: { columns: 'id, number, title, state, column_key' }
  })
    .then((res) => {
      const data = Array.isArray(res) ? res : (res?.data || res?.rows || [])
      const map = {}
      for (const t of (data || [])) map[t.number] = t
      for (const span of spans) {
        const num = parseInt(span.getAttribute('data-ref-ticket'), 10)
        const ticket = map[num]
        if (ticket) span.innerHTML = `<span>${_CARD_ICONS.ticket}</span> #${num} · ${(ticket.title || '').slice(0, 40)}`
      }
    }).catch(() => {})
}

const _EVENT_RE = /@@([a-zA-Z0-9._\- ]+)/g
const _highlightEvents = (el) => {
  const spans = _replaceTextNodes(el, _EVENT_RE, (match) => {
    const title = match[1].trim()
    const span = document.createElement('span')
    span.style.cssText = _CARD_STYLES.event
    span.setAttribute('data-ref-event', title)
    span.innerHTML = `<span>${_CARD_ICONS.event}</span> ${title}`
    return span
  })
  if (!spans.length) return
  const titles = [...new Set(spans.map(s => s.getAttribute('data-ref-event')))]
  // migrated 2026-04-27 to SDK route workspaceProject.calendar/list
  getSDK().execute('workspaceProject.calendar', 'list', {
    filter: { title_in: titles }
  })
    .then((res) => {
      const data = Array.isArray(res) ? res : (res?.data || res?.events || res?.rows || [])
      const map = {}
      for (const ev of (data || [])) {
        if (ev?.title) map[ev.title.toLowerCase()] = ev
      }
      for (const span of spans) {
        const title = span.getAttribute('data-ref-event')
        const ev = map[title.toLowerCase()]
        if (ev && ev.date) span.innerHTML = `<span>${_CARD_ICONS.event}</span> ${title} · ${ev.date}`
      }
    }).catch(() => {})
}

const _REF_RE = /\+([a-zA-Z0-9._\- ]+)/g
const _highlightReferences = (el) => {
  const spans = _replaceTextNodes(el, _REF_RE, (match) => {
    const title = match[1].trim()
    const span = document.createElement('span')
    span.style.cssText = _CARD_STYLES.reference
    span.setAttribute('data-ref-doc', title)
    span.innerHTML = `<span>${_CARD_ICONS.reference}</span> ${title}`
    return span
  })
  if (!spans.length) return
  const titles = [...new Set(spans.map(s => s.getAttribute('data-ref-doc')))]
  // migrated 2026-04-27 to SDK routes workspaceProject.documents.notes / .documents.kb
  Promise.all([
    getSDK().execute('workspaceProject.documents.notes', 'list', { filter: { title_in: titles } }),
    getSDK().execute('workspaceProject.documents.kb', 'list', { filter: { title_in: titles } })
  ]).then(([notesRes, kbRes]) => {
    const notesData = Array.isArray(notesRes) ? notesRes : (notesRes?.data || notesRes?.notes || notesRes?.rows || [])
    const kbData = Array.isArray(kbRes) ? kbRes : (kbRes?.data || kbRes?.articles || kbRes?.rows || [])
    const map = {}
    for (const n of (notesData || [])) {
      if (n?.title) map[n.title.toLowerCase()] = 'Note'
    }
    for (const a of (kbData || [])) {
      if (a?.title) map[a.title.toLowerCase()] = 'KB'
    }
    for (const span of spans) {
      const title = span.getAttribute('data-ref-doc')
      const type = map[title.toLowerCase()]
      if (type) span.innerHTML = `<span>${_CARD_ICONS.reference}</span> ${title} · ${type}`
    }
  }).catch(() => {})
}

// ── Core render helper — also used by the back-compat shim ─────────────────
// Exposed as a named export so renderMarkdown.js can delegate to it without
// importing the DOMQL component (avoids circular deps). Consumers calling the
// function path (el.call / imperative) always use this helper directly.
//
// Options:
//   withMentions   — boolean (default false)
//   demoteHeadings — boolean (default true)
//   people         — array of person objects for @mention resolution
//   channels       — array of channel objects for #channel resolution
//   components     — optional custom components map (overrides the demote/native map)
export const applyMarkdownToElement = (el, body, emptyNode, options = {}) => {
  const { withMentions = false, demoteHeadings = true, people, channels, components: customComponents } = options
  if (!el || typeof el.set !== 'function') return
  if (el._lastMd === body) return
  el._lastMd = body
  if (!body) {
    el.set(emptyNode || { tag: 'span', text: '' })
    return
  }
  const components = customComponents || (demoteHeadings ? _DEMOTE_COMPONENTS : _NATIVE_COMPONENTS)
  try {
    el.set(transformMarkdown(body, { components }))
  } catch (_) {
    el.set({ tag: 'span', text: body })
  }
  requestAnimationFrame(() => {
    // Run longer-prefix patterns FIRST (## before #, @@ before @).
    _TICKET_RE.lastIndex = 0
    if (_TICKET_RE.test(body)) _highlightTickets(el)

    _EVENT_RE.lastIndex = 0
    if (_EVENT_RE.test(body)) _highlightEvents(el)

    _REF_RE.lastIndex = 0
    if (_REF_RE.test(body)) _highlightReferences(el)

    if (withMentions) {
      const _MENTION_RE = /@([a-zA-Z0-9._-]+)/g
      _MENTION_RE.lastIndex = 0
      if (_MENTION_RE.test(body)) highlightMentions(el, people)

      const _CHANNEL_RE = /#([a-zA-Z0-9._-]+)/g
      _CHANNEL_RE.lastIndex = 0
      if (_CHANNEL_RE.test(body)) highlightChannels(el, channels)
    }
  })
}

// ── DOMQL v3.14 component ─────────────────────────────────────────────────────
// Props:
//   text           — markdown source
//   withMentions   — boolean (default false)
//   demoteHeadings — boolean (default true)
//
// The component relies on state to pass people/channels for mention highlighting.
// In chat contexts state carries s.root.people / s.root.channels automatically.
// In non-chat contexts those are simply absent and withMentions stays false.

const _render = (el, s) => {
  // el.text is the resolved markdown source string (v3.14 flat prop access).
  // withMentions / demoteHeadings are booleans set by the consumer.
  const body = el.text
  const withMentions = !!el.withMentions
  const demoteHeadings = el.demoteHeadings !== false
  const people = s && s.root && s.root.people
  const channels = s && s.root && s.root.channels
  applyMarkdownToElement(el, body || '', null, { withMentions, demoteHeadings, people, channels })
}

export const MarkdownPreview = {
  tag: 'div',
  onCreate: _render,
  onUpdate: _render
}
